import { DialogComponent } from '@theme/dialog';

/** Canonical service types that have panels. */
const CANONICAL_TYPES = new Set(['shipping-returns', 'client-service', 'track-order', 'return-order', 'appointment']);

/** Aliases map to canonical type for panel lookup. */
const TYPE_ALIASES = {
  'shipping-and-returns': 'shipping-returns',
  'client-services': 'client-service',
  'track-your-order': 'track-order',
  'return-your-order': 'return-order',
  'book-an-appointment': 'appointment',
};

/**
 * Dispatched to open the service drawer.
 * Declared before {@link ServiceDrawerComponent} so `customElements.define` does not run
 * `connectedCallback` while this binding is still in the temporal dead zone.
 *
 * @example document.dispatchEvent(new ServiceDrawerOpenEvent('shipping-returns'));
 */
export class ServiceDrawerOpenEvent extends CustomEvent {
  /** @param {string} serviceType */
  constructor(serviceType) {
    super(ServiceDrawerOpenEvent.eventName, { detail: { serviceType } });
  }

  get serviceType() {
    return this.detail?.serviceType ?? '';
  }

  static eventName = 'service-drawer:open';
}

/**
 * Bottom-sheet service drawer for footer links (Shipping & Returns, Client Service, etc.).
 * Uses DialogComponent for overlay, ESC, and click-outside mechanics.
 * Slides up from bottom with semi-transparent overlay.
 *
 * @extends {DialogComponent}
 */
class ServiceDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'title', 'content'];

  /** @type {string | null} */
  #currentType = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ServiceDrawerOpenEvent.eventName, this.#handleOpen);
    window.openServiceDrawer = (type) => this.open(type);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ServiceDrawerOpenEvent.eventName, this.#handleOpen);
    if (window.openServiceDrawer === this.open) {
      delete window.openServiceDrawer;
    }
  }

  /** @param {ServiceDrawerOpenEvent} event */
  #handleOpen = (event) => {
    this.open(event.serviceType);
  };

  /**
   * Opens the service drawer with the given type.
   * @param {string} [type] - Service type (e.g. 'shipping-returns', 'client-service'). Accepts aliases.
   */
  open(type = '') {
    const raw = String(type).toLowerCase().trim() || null;
    if (!raw) return;

    const normalizedType = TYPE_ALIASES[raw] || (CANONICAL_TYPES.has(raw) ? raw : null);
    if (!normalizedType) return;

    this.#currentType = normalizedType;
    this.#updateContent();

    this.showDialog();
    this.dispatchEvent(new CustomEvent('service-drawer:open', { bubbles: true }));
  }

  #updateContent() {
    const { title, content } = this.refs;
    if (!title || !content) return;

    const dataKey = 'title' + this.#toDataKey(this.#currentType);
    const activePanel = content.querySelector(`[data-service-type="${this.#currentType}"]`);
    const panelTitle = activePanel?.dataset?.serviceTitle;
    const titleText = this.dataset[dataKey] || panelTitle || this.#fallbackTitle(this.#currentType);
    title.textContent = titleText;

    const panels = content.querySelectorAll('[data-service-type]');
    for (const panel of panels) {
      panel.hidden = panel.getAttribute('data-service-type') !== this.#currentType;
    }
  }

  /** @param {string} type - e.g. 'shipping-returns' */
  #toDataKey(type) {
    return type
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('');
  }

  /** @param {string} type */
  #fallbackTitle(type) {
    const titles = {
      'shipping-returns': 'Shipping & returns',
      'client-service': 'Client service',
      'track-order': 'Track order',
      'return-order': 'Return order',
      appointment: 'Book an appointment',
    };
    return titles[type] ?? type;
  }
}

if (!customElements.get('service-drawer-component')) {
  customElements.define('service-drawer-component', ServiceDrawerComponent);
}

export { ServiceDrawerComponent };
